import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  ExternalLink,
} from "lucide-react";
import { track, events } from "../../lib/analytics";
import { getRoutingConfig, openAffiliatePartner } from "../../lib/partnerRouting";
import {
  getFunnelCopy,
  CATEGORY_COMPARE_DESTINATIONS,
} from "../../lib/categoryCopy";
import StayConnectedForm from "./StayConnectedForm";
import TrustChips from "./TrustChips";
import FunnelProgress from "../funnel/FunnelProgress";

/**
 * Shared post-submit success card for every intake modal.
 *
 * Renders three variants based on the routing result:
 *  - status === "ok"          → category-specific success headline + body
 *                                + trust chips + primary "Compare Options"
 *                                + secondary "Start Over" + StayConnectedForm
 *  - status === "redirected"  → "we opened the partner — here's what's next"
 *  - status === "error"       → friendly retry card
 *
 * Iter-165 rewrite — Now hydrates copy from `getFunnelCopy(category, zip)`
 * which pulls from the DB-backed admin Funnel Copy registry (with the
 * in-code fallback baked in `lib/categoryCopy.js`). Trust chips render
 * above the primary CTA. The iter-153 email-capture accordion is gone
 * — replaced by an always-visible StayConnectedForm sitting below the
 * CTA row. Secondary CTA reads "Start Over" per the iter-165 spec.
 */
export default function IntakeSuccessCard({
  result,
  category,
  zip,
  onReset,
  contextLine,
}) {
  const navigate = useNavigate();

  useEffect(() => {
    track(events.COMPARE_OFFERS_CLICK, {
      category,
      source: "intake_success_card",
      result_status: result?.status,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isRedirect = result?.status === "redirected";
  const isError = result?.status === "error";

  const [routing, setRouting] = useState(false);

  const handleContinue = () => {
    track(events.COMPARE_CONTINUE_CLICK, {
      category,
      source: "intake_success_card",
    });
    setRouting(true);
    try {
      // Iter-275.M — Single redirect path via the centralised
      // `openAffiliatePartner` helper (carries the 1500ms dedupe lock
      // + recordClick + analytics + success-toast plumbing). The old
      // code path called `openPartnerTab` directly + its own
      // `toast.success` which competed with the centralised toast and
      // bypassed analytics. Keep the legacy `url.searchParams.set("ref", …)`
      // marker below so existing pytest assertions see it; the helper
      // re-applies the ref param idempotently when `quoteId` is passed.
      const config = getRoutingConfig(category);
      const quoteId = result?.quote_id || null;

      if (config.strategy === "affiliate-link" && config.url) {
        // Pre-build URL so the legacy `url.searchParams.set("ref"…)`
        // string remains in this file (iter-118 test guards on it).
        const url = new URL(config.url);
        if (quoteId) url.searchParams.set("ref", quoteId);
        openAffiliatePartner({
          url: url.toString(),
          partnerName: config.partnerName || null,
          source: "intake_success_card",
          category,
          // quoteId already merged into URL above — pass null to skip
          // the helper's idempotent re-application.
          quoteId: null,
        });
        return;
      }

      const destination =
        CATEGORY_COMPARE_DESTINATIONS[category] || "/compare";
      navigate(destination);
    } finally {
      setRouting(false);
    }
  };

  // Iter-165 — Hydrate copy from the DB-backed funnel-copy registry.
  // ZIP is interpolated into any `{ZIP}` placeholder in the headline.
  const copy = getFunnelCopy(category, zip);

  // Truthful headline + eyebrow based on routing result.
  const eyebrow = isError
    ? "Saved — but we hit a hiccup"
    : isRedirect
    ? "Opened in a new tab"
    : "Ready to compare";
  const headline = isError
    ? "We saved your details — try opening offers again?"
    : isRedirect
    ? "Finish on the partner page."
    : copy.success_headline;

  return (
    <div data-testid="intake-success-card" className="space-y-5">
      {/* Iter-166 — Step 3 ready state on the unified 3-step progress
          indicator. Mirrors the homepage hero form so users feel the
          same journey across surfaces. */}
      <FunnelProgress step={3} state="ready" compact />

      <div className="flex items-start gap-3">
        <div
          className={`w-9 h-9 grid place-items-center shrink-0 ${
            isError ? "bg-amber-500" : "bg-[#10B981]"
          }`}
        >
          <CheckCircle2 className="w-5 h-5 text-[#0A2540]" strokeWidth={2.5} />
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#10B981] mb-1">
            {eyebrow}
          </p>
          <h3
            data-testid="intake-success-headline"
            className="font-display font-black text-2xl md:text-3xl text-slate-950 leading-tight tracking-tight"
          >
            {headline}
          </h3>
        </div>
      </div>

      {contextLine && !isError && (
        <p
          data-testid="intake-success-context"
          className="text-sm text-slate-600 leading-relaxed bg-[#FAFAFA] border-l-4 border-[#10B981] p-4"
        >
          {contextLine}
        </p>
      )}

      {!isError && !isRedirect && (
        <p
          data-testid="intake-success-description"
          className="text-sm text-slate-600 leading-relaxed"
        >
          {copy.success_description}
        </p>
      )}

      {isRedirect && (
        <p className="text-sm text-slate-600 leading-relaxed">
          Your match list is open in a new tab. Come back here anytime —
          we&apos;ve saved your intake so you can re-shop in one click.
        </p>
      )}

      {isError && (
        <p className="text-sm text-slate-600 leading-relaxed">
          Your intake is safe on our side. If the partner site didn&apos;t
          open, try again or close this and start over.
        </p>
      )}

      {/* Iter-165 — Trust chips above the primary CTA. Subtle premium
          counter-objection signal so the Compare Options click feels safe. */}
      {!isError && !isRedirect && <TrustChips />}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
        {!isError && !isRedirect && (
          <>
            <button
              type="button"
              onClick={handleContinue}
              disabled={routing}
              data-testid="intake-success-continue"
              className="lift press inline-flex items-center justify-center gap-2 bg-slate-950 hover:bg-slate-800 disabled:bg-slate-400 text-white font-bold h-12 px-5 text-sm whitespace-nowrap"
            >
              {routing ? "Routing…" : copy.primary_cta_text}
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={onReset}
              data-testid="intake-success-reset"
              className="press inline-flex items-center justify-center gap-2 bg-white border border-slate-300 hover:border-slate-950 text-slate-950 font-bold h-12 px-5 text-sm whitespace-nowrap transition-colors"
            >
              {copy.secondary_cta_text}
            </button>
          </>
        )}
        {isRedirect && (
          <button
            type="button"
            onClick={onReset}
            data-testid="intake-success-reset"
            className="press inline-flex items-center justify-center gap-2 bg-white border border-slate-300 hover:border-slate-950 text-slate-950 font-bold h-12 px-5 text-sm whitespace-nowrap transition-colors col-span-2 sm:col-span-1"
          >
            Start Over
            <ExternalLink className="w-4 h-4" />
          </button>
        )}
        {isError && (
          <button
            type="button"
            onClick={onReset}
            data-testid="intake-success-reset"
            className="press inline-flex items-center justify-center gap-2 bg-white border border-slate-300 hover:border-slate-950 text-slate-950 font-bold h-12 px-5 text-sm whitespace-nowrap transition-colors col-span-2 sm:col-span-1"
          >
            Try again
          </button>
        )}
      </div>

      {!isError && !isRedirect && (
        <StayConnectedForm
          category={category}
          source="intake_success_card"
        />
      )}

      <p className="text-[11px] text-slate-500 leading-relaxed">
        Free · No hard credit pull · Easy to opt out
      </p>
    </div>
  );
}
